import { formatDateToDateString } from "@components/inputs/date/utils";
import { SwitchType } from "@components/inputs/switch/Switch";
import {
    getSimpleBoolItemLabel,
    getSimpleBoolSelectItems,
    ifAll,
    IFieldDefFn,
    IGetValueArgs,
    not,
    TTemporalDialogSettings
} from "@components/smart/FieldInfo";
import {
    BirthNumberFieldDef,
    EmailDef,
    getAddressFields,
    getAddressGroup,
    PhoneNumberDef
} from "@components/smart/GeneralFieldDefinition";
import {
    CompanyBankAccountEntity,
    CompanyCommunicationContactEntity,
    CompanyEntity,
    CompanyModuleEntity,
    CompanyVatReducedCoefficientEntity,
    CompanyVatStatusTemporalCurrentEntity,
    CompanyVatStatusTemporalEntity,
    CountryRegionEntity,
    CzNaceEntity,
    EntitySetName,
    EntityTypeName,
    GovernmentInstitutionEntity,
    ICompanyEntity,
    ICompanyVatStatusTemporalEntity,
    TaxStatementSignatoryPersonEntity
} from "@odata/GeneratedEntityTypes";
import {
    AccountingCode,
    CompanyBankAccountTypeCode,
    CountryCode,
    FeatureCode,
    GovernmentInstitutionTypeCode,
    TypeOfBusinessCode,
    VatStatementFrequencyCode,
    VatStatusCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName, getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import {
    dateValidFromValidator,
    getTemporalAdditionalProperties,
    getTemporalPropertyExtraFieldContentAfter,
    getTemporalPropertyFieldDefinition,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { hasEverBeenOrWillBeOssCompany } from "@pages/documents/Oss.global.utils";
import {
    generalBankAccountFormatter,
    getBankDependentFields,
    getCompanyBankAccountNavigationFieldDefinition
} from "@utils/BankUtils";
import { isNewEntity } from "@utils/definition.utils";
import i18next from "i18next";
import React from "react";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    CacheStrategy,
    FastEntryInputSizes,
    FieldType,
    GroupedField,
    LabelStatus,
    NavigationSource,
    TextAlign,
    ValidatorType
} from "../../enums";
import { Title2 } from "../../global.style";
import { TValue } from "../../global.types";
import BindingContext, { createPath } from "../../odata/BindingContext";
import { DATE_MIN, getUtcDate } from "../../types/Date";
import { IFormDef } from "../../views/formView/Form";
import { FormStorage } from "../../views/formView/FormStorage";
import { setDefByEntityType } from "../getDefByEntityType";
import { IDefinition, IGetDefinition } from "../PageUtils";
import { getFinancialAdministrationDef } from "../payroll/governmentInstitution/GovernmentInstitution.utils";
import { companyIsSecondaryAddressPath, isSecondaryAddressVisible } from "./Company.shared.utils";
import { ICompanyExtendedEntity, ICompanyFormCustomData, TCompanyFormStorage } from "./Company.types";
import {
    canEditCsszIdentificationNumber,
    canEditOtherInstitutionsIdentificationNumber,
    canEditDefaultStatutoryInsuranceCompanyRate,
    CompanySingleBusinessPartnerDef,
    companyTypeSettingsPath,
    getCompanyGovernmentInstitutionDef,
    getIsNonVatRegisteredFieldDisabled,
    getVatDeductionCoefficientCurrentYear,
    hasPayrollEnabled,
    isNotLastVatStatus,
    isVatRegistered,
    ossBankAccountPath,
    validatorVatFrequencyMonthlyDate,
    validatorVatFrequencyQuarterlyDate,
    VatReducedDeductionCoefficient
} from "./Company.utils";
import CompanyFormAvatarWithName from "./CompanyFormAvatarWithName";
import CompanyTypeSettings from "./CompanyTypeSettings";
import {
    getTaxStatementSignatoryPersonFieldDefs,
    hasTaxStatementSignatoryPersonPath
} from "./TaxStatementSignatoryPerson.utils";

const isNonProfit = (args:IGetValueArgs) => {
    return args.storage.data.entity.AccountingCode === AccountingCode.AccountingForNonprofitOrganizations;
};

const isEdit = (args: IGetValueArgs) => {
    return !args.storage.data.bindingContext.isNew();
};

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const legalAddressFields = getAddressFields(CompanyEntity.LegalAddress, {}, { addHouseStreetNumber: true });
    const communicationAddressFields = getAddressFields(CompanyEntity.CommunicationAddress, { isVisible: isSecondaryAddressVisible }, { addHouseStreetNumber: true });
    const isPayrollEnabled = context.isFeatureSwitchEnabled(FeatureCode.Payroll);

    const temporalDialogSettings: TTemporalDialogSettings = {
        dialogTitle: i18next.t("Companies:Form.VatStatusPlanDialog"),
        columns: [
            CompanyVatStatusTemporalEntity.VatStatus,
            CompanyVatStatusTemporalEntity.IsOssMode,
            CompanyVatStatusTemporalEntity.VatStatementFrequency
        ],
        granularity: "day",
        withoutGaps: true,
        cannotBeEnded: true,
        // we don't make rows readonly in VatStatus configuration, checks are too complex
        // so lets BE decide if the configuration is valid or not
        decisiveDateForHistoryValues: DATE_MIN
    };

    const financialAdministrationDef = getFinancialAdministrationDef();
    const ossBankAccountDef = getCompanyBankAccountNavigationFieldDefinition({
        allowedCompanyBankAccountTypes: [CompanyBankAccountTypeCode.Bank]
    });


    const form: IFormDef = {
        id: `${EntityTypeName.Company}Form`,
        isReadOnly: (args: IGetValueArgs) => !(args.storage as FormStorage<ICompanyEntity, ICompanyFormCustomData>).getCustomData().hasPermission,
        translationFiles: getDefinitions.translationFiles,
        fieldDefinition: {
            LegalName: {
                ...CompanySingleBusinessPartnerDef,
                groupedField: GroupedField.MultiStart,
                width: BasicInputSizes.XL,
                affectedFields: [{ id: createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatementFrequency) }]
            },
            LegalNumber: {
                ...CompanySingleBusinessPartnerDef,
                groupedField: GroupedField.MultiEnd,
                width: BasicInputSizes.S,
                affectedFields: [{ id: createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatementFrequency) }]
            },
            TaxNumber: {
                width: BasicInputSizes.M,
                isRequired: (args: IGetValueArgs<boolean>) => {
                    const entity = args.storage.getEntity<ICompanyExtendedEntity>();

                    if (entity.TypeOfBusinessCode === TypeOfBusinessCode.LegalPerson) {
                        return true;
                    }

                    return entity.CurrentTemporalPropertyBag?.VatStatusCode === VatStatusCode.IdentifiedPerson;
                }
            },
            BirthNumber: {
                ...BirthNumberFieldDef,
                isVisible: (args: IGetValueArgs) => {
                    const companyEntity = args.storage.getEntity<ICompanyEntity>();
                    return companyEntity.TypeOfBusinessCode === TypeOfBusinessCode.NaturalPerson;
                }
            },
            ...legalAddressFields,
            "LegalAddress/Country": {
                ...legalAddressFields["LegalAddress/Country"],
                width: BasicInputSizes.L,
                defaultValue: CountryCode.CzechRepublic
            },
            ...communicationAddressFields,
            "CommunicationAddress/Country": {
                ...communicationAddressFields["CommunicationAddress/Country"],
                width: BasicInputSizes.L
            },
            [companyIsSecondaryAddressPath]: {
                label: i18next.t("Companies:Form.AddAnotherAddress"),
                type: FieldType.Switch,
                fieldSettings: { type: SwitchType.YesNo },
                affectedFields: [
                    { id: "CommunicationAddress/Street" }, { id: "CommunicationAddress/City" },
                    { id: "CommunicationAddress/PostalCode" }, { id: "CommunicationAddress/Country" }
                ]
            },
            "CommunicationContact/FirstName": {
                groupedField: GroupedField.MultiStart,
                isRequired: true
            },
            "CommunicationContact/LastName": {
                groupedField: GroupedField.MultiEnd,
                isRequired: true
            },
            "CommunicationContact/Email": {
                ...EmailDef,
                width: BasicInputSizes.XL,
                isRequired: true
            },
            "CommunicationContact/PhoneNumber": {
                ...PhoneNumberDef,
                isRequired: true
            },
            // we don't want new entity TemporalPropertyBag to be null
            // => use default value
            [CompanyEntity.TemporalPropertyBag]: {
                defaultValue: []
            },
            ...getTemporalPropertyFieldDefinition({
                propName: CompanyVatStatusTemporalCurrentEntity.VatStatus,
                definition: {
                    type: FieldType.ComboBox,
                    cacheStrategy: CacheStrategy.EndOfTime,
                    defaultValue: VatStatusCode.VATRegistered,
                    useForValidation: true,
                    fieldSettings: {
                        displayName: "Name",
                        items: getEnumSelectItems(EntityTypeName.VatStatus),
                        temporalDialog: temporalDialogSettings
                    },
                    isDisabled: isNotLastVatStatus,
                    affectedFields: [
                        { id: createPath(CompanyEntity.TemporalPropertyBag, CompanyVatStatusTemporalEntity.VatStatementFrequency) },
                        { id: createPath(CompanyEntity.TemporalPropertyBag, CompanyVatStatusTemporalEntity.IsOssMode) }
                    ],
                    width: FastEntryInputSizes.M,
                    groupedField: GroupedField.MultiStart
                },
                currentDefinition: {
                    label: i18next.t("Companies:Form.CurrentVatStatus"),
                    defaultValue: VatStatusCode.VATRegistered,
                    isDisabled: (args: IGetValueArgs) => {
                        return !args.bindingContext.getRootParent().isNew();
                    },
                    groupedField: (args: IGetValueArgs) => {
                        // only MultiStart if frequency is visible
                        if (isVatRegistered(args)) {
                            return GroupedField.MultiStart;
                        }

                        return null;
                    },
                    additionalProperties: [
                        { id: `/${createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.DateValidFrom)}` },
                        { id: `/${createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.DateValidTo)}` },
                        { id: `/${createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatus)}` },
                        { id: `/${createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatementFrequency)}` }
                    ],
                    affectedFields: [
                        { id: createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatementFrequency) },
                        { id: VatReducedDeductionCoefficient }
                    ],
                    extraFieldContentAfter: (args: IGetValueArgs) => {
                        // only MultiStart if frequency is visible
                        if (args.bindingContext.getRootParent().isNew() || isVatRegistered(args)) {
                            return null;
                        }

                        return getTemporalPropertyExtraFieldContentAfter()(args);
                    }
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: CompanyVatStatusTemporalCurrentEntity.VatStatementFrequency,
                definition: {
                    type: FieldType.ComboBox,
                    defaultValue: VatStatementFrequencyCode.Monthly,
                    useForValidation: true,
                    fieldSettings: {
                        displayName: "Name",
                        temporalDialog: temporalDialogSettings
                    },
                    formatter: (val: TValue, args: IFormatOptions) => {
                        const vatStatus = args.storage.getValue(args.bindingContext.getParent()) as ICompanyVatStatusTemporalEntity;

                        return vatStatus?.VatStatusCode === VatStatusCode.VATRegistered ? getEnumDisplayValue(EntityTypeName.VatStatementFrequency, vatStatus?.VatStatementFrequency?.Code) : "";
                    },
                    isDisabled: getIsNonVatRegisteredFieldDisabled(),
                    groupedField: GroupedField.MultiEnd
                },
                currentDefinition: {
                    label: i18next.t("Companies:Form.CurrentVatStatusFrequency"),
                    defaultValue: VatStatementFrequencyCode.Monthly,
                    isVisible: isVatRegistered,
                    isDisabled: (args: IGetValueArgs) => {
                        return !args.bindingContext.getRootParent().isNew();
                    },
                    extraFieldContentAfter: (args: IGetValueArgs) => {
                        if (args.bindingContext.getRootParent().isNew()) {
                            return null;
                        }

                        return getTemporalPropertyExtraFieldContentAfter()(args);
                    }
                }
            }),
            [createPath(CompanyEntity.TemporalPropertyBag, CompanyVatStatusTemporalEntity.DateValidFrom)]: {
                ...TemporalValidityFieldsDef["TemporalPropertyBag/DateValidFrom"],
                isDisabled: isNotLastVatStatus,
                width: FastEntryInputSizes.M,
                validator: {
                    type: ValidatorType.Date,
                    settings: {
                        min: 0,
                        customValidator: [
                            {
                                validator: validatorVatFrequencyQuarterlyDate,
                                message: i18next.t("Error:CompanyVatStatusSwitchFromMonthFrequencyToQuarterDateIsInvalid")
                            },
                            {
                                validator: validatorVatFrequencyMonthlyDate,
                                message: i18next.t("Error:CompanyVatStatusSwitchFromQuarterFrequencyToMonthDateIsInvalid")
                            },
                            {
                                validator: dateValidFromValidator
                            }
                        ]
                    }
                }
            },
            [createPath(CompanyEntity.TemporalPropertyBag, CompanyVatStatusTemporalEntity.DateValidTo)]: {
                ...TemporalValidityFieldsDef["TemporalPropertyBag/DateValidTo"],
                width: FastEntryInputSizes.M,
                isDisabled: isNotLastVatStatus,
            },
            TypeOfBusiness: {
                type: FieldType.ComboBox,
                cacheStrategy: CacheStrategy.EndOfTime,
                defaultValue: TypeOfBusinessCode.LegalPerson,
                isDisabled: (args: IGetValueArgs) => {
                    const ac = args.storage.data.entity.Accounting?.Code;
                    return ac !== AccountingCode.AccountingForBusiness;
                },
                fieldSettings: {
                    displayName: "Name"
                },
                affectedFields: [
                    { id: CompanyEntity.BirthNumber },
                    { id: CompanyEntity.Accounting },
                    { id: CompanyEntity.TaxNumber }
                ]
            },
            CzNace: {
                type: FieldType.ComboBox,
                cacheStrategy: CacheStrategy.EndOfTime,
                width: BasicInputSizes.XL,
                filter: {
                    select: () => {
                        const today = formatDateToDateString(getUtcDate());
                        return `${CzNaceEntity.DateValidFrom} le ${today} and (${CzNaceEntity.DateValidTo} ge ${today} or ${CzNaceEntity.DateValidTo} eq null)`;
                    }
                },
                fieldSettings: {
                    displayName: CzNaceEntity.Description,
                    additionalProperties: [{ id: CzNaceEntity.Rate }],
                    localDependentFields: [{
                        from: { id: CzNaceEntity.Rate },
                        to: { id: CzNaceEntity.Rate },
                        navigateFrom: NavigationSource.Itself
                    }]
                },
                columns: [
                    { id: CzNaceEntity.Description },
                    { id: CzNaceEntity.MpsvCode }
                ],
                affectedFields: [{
                    id: CompanyEntity.DefaultStatutoryInsuranceCompanyRate
                }]
            },
            [BindingContext.localContext("Edition")]: {
                label: i18next.t("Companies:Form.Edition"),
                type: FieldType.ComboBox,
                isRequired: true,
                isDisabled: true,
                isReadOnly: isEdit,
                defaultValue: "czech",
                fieldSettings: {
                    items: [
                        {
                            id: "czech",
                            label: i18next.t("Companies:Form.CzechEdition")
                        }
                    ]
                }
            },
            CompanyRegister: {
                type: FieldType.EditableText,
                width: BasicInputSizes.XL,
                isConfirmable: true,
                fieldSettings: {
                    placeholder: i18next.t("Companies:Form.CompanyRegisterPlaceholder")
                }
            },
            [CompanyEntity.FinancialAdministration]: {
                ...financialAdministrationDef,
                width: BasicInputSizes.L,
                label: isPayrollEnabled ? i18next.t("Companies:Form.FinancialAdministrationNew") : i18next.t("Companies:Form.FinancialAdministration"),
                isRequired: true,
                additionalProperties: [{ id: GovernmentInstitutionEntity.Ufo }, { id: GovernmentInstitutionEntity.DataBoxId }],
                filter: {
                    select: () => {
                        return `${GovernmentInstitutionEntity.GovernmentInstitutionTypeCode} eq '${GovernmentInstitutionTypeCode.FinancialAdministration}'`;
                    }
                }
            },
            [CompanyEntity.SocialSecurity]: {
                ...getCompanyGovernmentInstitutionDef(GovernmentInstitutionTypeCode.CzechSocialSecurityAdministration)
            },
            [CompanyEntity.StatutoryInsuranceCompany]: {
                ...getCompanyGovernmentInstitutionDef(GovernmentInstitutionTypeCode.StatutoryInsuranceCompany)
            },
            [CompanyEntity.LaborOffice]: {
                ...getCompanyGovernmentInstitutionDef(GovernmentInstitutionTypeCode.LaborOffice)
            },
            [CompanyEntity.CsszIdentificationNumber]: {
                width: BasicInputSizes.L,
                label: i18next.t("Companies:Form.CsszIdentificationNumber"),
                isVisible: ifAll(hasPayrollEnabled, canEditCsszIdentificationNumber),
                isRequired: ifAll(hasPayrollEnabled, canEditCsszIdentificationNumber),
                clearIfInvisible: false
            },
            [CompanyEntity.OtherInstitutionsIdentificationNumber]: {
                width: BasicInputSizes.L,
                label: i18next.t("Companies:Form.OtherInstitutionsIdentificationNumber"),
                isVisible: ifAll(hasPayrollEnabled, canEditOtherInstitutionsIdentificationNumber),
                isRequired: ifAll(hasPayrollEnabled, canEditOtherInstitutionsIdentificationNumber),
                clearIfInvisible: false
            },
            [CompanyEntity.DefaultStatutoryInsuranceCompanyRate]: {
                type: FieldType.NumberInput,
                width: BasicInputSizes.L,
                isVisible: ifAll(hasPayrollEnabled, canEditDefaultStatutoryInsuranceCompanyRate),
                isRequired: ifAll(hasPayrollEnabled, canEditDefaultStatutoryInsuranceCompanyRate),
                clearIfInvisible: false,
                fieldSettings: {
                    unit: "‰",
                    min: 0,
                    max: 1000
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        max: 1000
                    }
                }
            },
            [CompanyEntity.RegionForVzp]: {
                type: FieldType.ComboBox,
                cacheStrategy: CacheStrategy.EndOfTime,
                width: BasicInputSizes.L,
                label: i18next.t("Companies:Form.RegionForVzp"),
                fieldSettings: {
                    displayName: "Name"
                },
                filter: {
                    select: () => {
                        return `${CountryRegionEntity.CountryCode} eq '${CountryCode.CzechRepublic}'`;
                    }
                },
                isVisible: hasPayrollEnabled,
                isRequired: hasPayrollEnabled,
                clearIfInvisible: false
            },
            [CompanyEntity.Name]: {
                type: FieldType.WriteLine,
                labelStatus: LabelStatus.Hidden,
                isRequired: false,
                isConfirmable: true,
                fieldSettings: {
                    placeholder: i18next.t("Companies:Form.NameWatermark")
                },
                textAlign: TextAlign.Left,
                width: BasicInputSizes.L,
                render: ({ storage, fieldElement }: IFieldDefFn) => {
                    return (
                        <CompanyFormAvatarWithName storage={storage as FormStorage} fieldElement={fieldElement}/>
                    );
                }
            },
            [CompanyEntity.UseVatReducedDeduction]: {
                type: FieldType.Switch,
                label: i18next.t("Companies:Form.VatReducedDeduction"),
                isVisible: ifAll(isVatRegistered, not(isNewEntity)),
                affectedFields: [{ id: VatReducedDeductionCoefficient }]
            },
            [VatReducedDeductionCoefficient]: {
                type: FieldType.NumberInput,
                isRequired: true,
                label: i18next.t("Companies:Form.Value"),
                tooltip: () => i18next.t("Companies:Form.ValueTooltip", { year: getVatDeductionCoefficientCurrentYear() }),
                width: BasicInputSizes.S,
                fieldSettings: {
                    unit: "%",
                    showSteppers: true,
                    min: 0,
                    max: 100
                },
                validator: {
                    type: ValidatorType.Integer,
                    settings: {
                        min: 0,
                        max: 100
                    }
                },
                additionalProperties: [
                    { id: createPath(CompanyEntity.VatReducedCoefficients, CompanyVatReducedCoefficientEntity.Year) },
                    { id: createPath(CompanyEntity.VatReducedCoefficients, CompanyVatReducedCoefficientEntity.VatReducedCoefficient) }
                ],
                isVisible: (args: IGetValueArgs<ICompanyEntity>) => !!args.storage?.data.entity?.UseVatReducedDeduction && isVatRegistered(args)
            },
            ...(getTaxStatementSignatoryPersonFieldDefs()),
            ...getTemporalPropertyFieldDefinition({
                propName: CompanyVatStatusTemporalCurrentEntity.IsOssMode,
                definition: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.S,
                    fieldSettings: {
                        items: getSimpleBoolSelectItems(),
                        temporalDialog: temporalDialogSettings
                    },
                    defaultValue: false,
                    formatter: (val: TValue, args: IFormatOptions) => {
                        const vatStatus = args.storage.getValue(args.bindingContext.getParent()) as ICompanyVatStatusTemporalEntity;

                        return vatStatus?.VatStatusCode === VatStatusCode.VATRegistered ? getSimpleBoolItemLabel(val as boolean) : "";
                    },
                    isDisabled: getIsNonVatRegisteredFieldDisabled(true),
                    isRequired: not(getIsNonVatRegisteredFieldDisabled(true)),
                    isVisible: ifAll(isVatRegistered, not(isNonProfit))
                },
                currentDefinition: {
                    isVisible: ifAll(isVatRegistered, not(isNonProfit)),
                    isDisabled: (args: IGetValueArgs) => {
                        return !args.bindingContext.getRootParent().isNew();
                    },
                    isRequired: true,
                    affectedFields: [
                        { id: createPath(ossBankAccountPath, "Id") }
                    ]
                },
                withOpeningButton: false
            }),
            [createPath(ossBankAccountPath, "Id")]: {
                ...ossBankAccountDef,
                label: i18next.t("Companies:Form.OssBankAccount"),
                additionalProperties: [],
                formatter: (val: TValue, args?: IFormatOptions) => {
                    // since we sadly have to use local context field, cuz for some reason BE couldn't add this property as navigation directly in company,
                    // fake args.item to contain the bank account data, for the formatter to work
                    const localContextBankAccount = args?.storage?.getValueByPath(ossBankAccountPath) ?? {};
                    const cleanBankAccount = Object.entries(localContextBankAccount).reduce((acc, [key, value]) => {
                        acc[BindingContext.cleanLocalContext(key)] = value;

                        return acc;
                    }, {} as Record<string, unknown>);


                    return generalBankAccountFormatter(val, {
                        ...args,
                        item: cleanBankAccount
                    });
                },
                filter: {
                    select: `${CompanyBankAccountEntity.CompanyBankAccountTypeCode} eq '${CompanyBankAccountTypeCode.Bank}'`
                },
                fieldSettings: {
                    ...ossBankAccountDef.fieldSettings,
                    noRecordText: i18next.t("Common:General.Default"),
                    localDependentFields: getBankDependentFields(ossBankAccountPath)
                },
                isVisible: (args: IGetValueArgs) => {
                    if (args.storage.data.bindingContext.getRootParent().isNew()) {
                        return false;
                    }

                    const company = args.storage.getEntity<ICompanyEntity>();

                    return hasEverBeenOrWillBeOssCompany(args.storage.context, company);
                },
                isDisabled: (args: IGetValueArgs) => {
                    return !!args.storage.data.bindingContext.getRootParent().isNew();
                }
            },
            ...(getTaxStatementSignatoryPersonFieldDefs()),
            [companyTypeSettingsPath]: {
                type: FieldType.Custom,
                render: (args: IFieldDefFn) => {
                    const storage = args.storage as TCompanyFormStorage;

                    return (
                        <>
                            <CompanyTypeSettings storage={storage}/>
                            <Title2>
                                {i18next.t("Companies:Form.BasicInfoLabel")}
                            </Title2>
                        </>
                    );
                }
            }
        },
        groups: [
            {
                id: "companyTypeSettings",
                rows: [[{ id: companyTypeSettingsPath }]]
            }, {
                id: "header",
                rows: [[{ id: CompanyEntity.Name }]]
            }, {
                id: "main",
                rows: [
                    [{ id: CompanyEntity.LegalName }, { id: CompanyEntity.LegalNumber }, { id: CompanyEntity.TaxNumber }],
                    ...getAddressGroup(CompanyEntity.LegalAddress, { addHouseStreetNumber: true }),
                    [{ id: companyIsSecondaryAddressPath }],
                    ...getAddressGroup(CompanyEntity.CommunicationAddress, { addHouseStreetNumber: true })
                ]
            }, {
                id: "Contact",
                title: i18next.t("Companies:Form.Contact"),
                rows: [[{ id: createPath(CompanyEntity.CommunicationContact, CompanyCommunicationContactEntity.FirstName) }, { id: createPath(CompanyEntity.CommunicationContact, CompanyCommunicationContactEntity.LastName) },
                    { id: createPath(CompanyEntity.CommunicationContact, CompanyCommunicationContactEntity.PhoneNumber) }, { id: createPath(CompanyEntity.CommunicationContact, CompanyCommunicationContactEntity.Email) }]]
            }, {
                id: "Settings",
                title: i18next.t("Companies:Form.Settings"),
                rows: [
                    [{ id: CompanyEntity.TypeOfBusiness }, { id: CompanyEntity.BirthNumber }],
                    [
                        { id: createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatus) },
                        { id: createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.IsOssMode) },
                        { id: createPath(CompanyEntity.CurrentTemporalPropertyBag, CompanyVatStatusTemporalCurrentEntity.VatStatementFrequency) },
                        { id: CompanyEntity.UseVatReducedDeduction },
                        { id: VatReducedDeductionCoefficient }
                    ],
                    [{ id: createPath(ossBankAccountPath, "Id") }],
                    [{ id: CompanyEntity.CzNace }],
                    [{ id: CompanyEntity.CompanyRegister }],
                    [{ id: CompanyEntity.FinancialAdministration }]
                ]
            }, {
                id: "PayrollSettings",
                title: i18next.t("Companies:Form.PayrollSettings"),
                rows: [
                    [
                        { id: CompanyEntity.CsszIdentificationNumber },
                        { id: CompanyEntity.OtherInstitutionsIdentificationNumber },
                        { id: CompanyEntity.DefaultStatutoryInsuranceCompanyRate },
                    ],
                    [
                        { id: CompanyEntity.SocialSecurity },
                        { id: CompanyEntity.LaborOffice },
                        { id: CompanyEntity.StatutoryInsuranceCompany },
                        { id: CompanyEntity.RegionForVzp }
                    ]
                ]
            }, {
                id: "TaxStatementSignatoryPerson",
                togglePropPath: hasTaxStatementSignatoryPersonPath,
                isVisible: (args: IGetValueArgs) => {
                    return !args.storage?.data.bindingContext.isNew() && !args.storage.context.getAddingNewCompany();
                },
                title: i18next.t("Companies:Form.TaxStatementSignatoryPerson"),
                rows: [[{
                    id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.TaxStatementSubmitter)
                }, {
                    id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.PersonType)
                }],
                    [{
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.LegalPersonName)
                    }, {
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.LegalNumber)
                    }],
                    [{
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.FirstName)
                    }, {
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.LastName)
                    }],
                    [{
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.RelationshipToTaxSubject)
                    }],
                    [{
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.DateOfBirth)
                    }, {
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.TaxAdvisorRegistrationNumber)
                    }],
                    [{
                        id: createPath(CompanyEntity.TaxStatementSignatoryPerson, TaxStatementSignatoryPersonEntity.SignatoryPerson)
                    }]
                ]
            }
        ],
        additionalProperties: [
            ...getTemporalAdditionalProperties(CompanyVatStatusTemporalCurrentEntity, CompanyEntity),
            { id: createPath(CompanyEntity.CzNace, CzNaceEntity.Rate) },
            {
                id: CompanyEntity.Name,
                useForValidation: true
            },
            { id: CompanyEntity.Logo },
            { id: CompanyEntity.AccountingCode },
            {
                id: CompanyEntity.Modules,
                additionalProperties: [{
                    id: CompanyModuleEntity.ModuleCode
                }, {
                    id: CompanyModuleEntity.IsEnabled
                }]
            }
        ]
    };

    return {
        entitySet: EntitySetName.Companies,
        form
    };
};

getDefinitions.translationFiles = ["Subscriptions", "Companies", "Common", "Components", "Error", getEnumNameSpaceName(EntityTypeName.VatStatementFrequency), getEnumNameSpaceName(EntityTypeName.VatStatus)];
setDefByEntityType(EntityTypeName.Company, getDefinitions);